import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, EffectNotification, ofType, OnRunEffects } from "@ngrx/effects";
import { catchError, EMPTY, filter, from, Observable, switchMap } from "rxjs";
import { SwPush } from "@angular/service-worker";
import { authServiceActions, PcpAuthService } from "@pcp/auth";
import { combineLatestWith } from "rxjs/operators";
import { WebPushHelperService } from "../services/web-push-helper.service";
import { toObservable } from "@angular/core/rxjs-interop";
import { WEB_PUSH_ENABLED } from "../injection-tokens";
import { PCP_LOGGER } from "@pcp/logging";

@Injectable()
export class SubscribeWebPushEffects implements OnRunEffects {

    ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>): Observable<EffectNotification> {

        // WebPush is disabled by FeatureFlag
        if (!this.featureWebPushEnabled) {
            return EMPTY;
        }

        // we need both ServiceWorker and PushManager
        if (!this.webPushHelperService.pushManagerEnabled()) {
            return EMPTY;
        }

        return resolvedEffects$;
    }

    private readonly log = inject(PCP_LOGGER).createScoped('service-worker');
    private readonly featureWebPushEnabled = inject(WEB_PUSH_ENABLED);
    private readonly swPush = inject(SwPush);
    private readonly webPushHelperService = inject(WebPushHelperService);
    private readonly authService = inject(PcpAuthService);
    private readonly actions$ = inject(Actions);

    private readonly permissionState$ = toObservable(this.webPushHelperService.permissionState);

    /**
     * https://angular.io/guide/service-worker-communications#version-updates
     *
     * TODO: delay permission request e.g. until the app is stable -> some browser require a user interaction first
     */
    readonly showUpdatePrompt$ = createEffect(() => this.authService.isAuthenticated$.pipe(
        combineLatestWith(this.swPush.subscription, this.permissionState$),
        filter(([isAuthenticated, subscription, permissionState]) => isAuthenticated && !subscription && permissionState === 'granted'),
        switchMap(() => this.webPushHelperService.createAndSaveSubscription()),
    ), { dispatch: false });

    readonly unsubscribeFromWebPush$ = createEffect(() => this.actions$.pipe(
        ofType(authServiceActions.userHasBeenLoggedOut),
        switchMap(() => from(this.swPush.unsubscribe()).pipe(
            catchError(error => {
                this.log.error('Error while unsubscribing from Web Push', error instanceof Error ? error : undefined, { error });
                return EMPTY;
            }
        ))),
    ), { dispatch: false });
}
